/*
  VNI Privacy & Website Compliance
  Accessible, dependency-free enhancements for navigation, search, copy tools,
  reading progress, print controls, and the optional privacy request form.
*/

(() => {
  'use strict';

  const ELEMENT_NAME = 'vni-privacy-compliance';
  const STORAGE_KEY = 'vni-policy-reading-mode';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  class VNIPrivacyCompliance extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.resizeObserver = null;
      this.rafId = 0;
      this.toastTimer = 0;
      this.searchTimer = 0;
      this.headerElement = null;
      this.sections = [];
      this.links = [];
      this.currentSectionId = '';
      this.searchMarks = [];
      this.searchCursor = -1;
      this.initialized = false;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;

      this.documentElement = this.querySelector('[data-vni-policy-document]');
      this.progressElement = this.querySelector('[data-vni-policy-progress]');
      this.searchInput = this.querySelector('[data-vni-policy-search]');
      this.searchClear = this.querySelector('[data-vni-policy-search-clear]');
      this.searchCount = this.querySelector('[data-vni-policy-search-count]');
      this.jumpSelect = this.querySelector('[data-vni-policy-jump]');
      this.readingModeButton = this.querySelector('[data-vni-policy-reading-mode]');
      this.printButton = this.querySelector('[data-vni-policy-print]');
      this.floatingTop = this.querySelector('[data-vni-policy-floating-top]');
      this.toast = this.querySelector('[data-vni-policy-toast]');
      this.sections = Array.from(this.querySelectorAll('[data-vni-policy-section]'));
      this.links = Array.from(this.querySelectorAll('[data-vni-policy-link]'));

      this.findHeader();
      this.restoreReadingMode();
      this.bindAnchorNavigation();
      this.bindUtilityControls();
      this.bindCopyControls();
      this.bindRequestForm();
      this.bindViewportEvents();
      this.updateLayoutMetrics();
      this.updateViewportState();

      const successMessage = this.querySelector('[data-vni-policy-form-success]');
      if (successMessage) {
        window.setTimeout(() => {
          successMessage.focus({ preventScroll: true });
          this.scrollToElement(successMessage, 28);
        }, 150);
      }
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      window.cancelAnimationFrame(this.rafId);
      window.clearTimeout(this.toastTimer);
      window.clearTimeout(this.searchTimer);
      this.clearSearchHighlights();
      this.initialized = false;
    }

    findHeader() {
      this.headerElement = document.querySelector(
        '[data-vni-header] .vni-header__shell, .vni-header__shell, .shopify-section-group-header-group'
      );

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scheduleViewportUpdate(true));
        this.resizeObserver.observe(this);
        if (this.headerElement) this.resizeObserver.observe(this.headerElement);
      }
    }

    bindViewportEvents() {
      window.addEventListener('scroll', () => this.scheduleViewportUpdate(false), {
        passive: true,
        signal: this.signal
      });

      window.addEventListener('resize', () => this.scheduleViewportUpdate(true), {
        passive: true,
        signal: this.signal
      });

      window.addEventListener('orientationchange', () => this.scheduleViewportUpdate(true), {
        passive: true,
        signal: this.signal
      });
    }

    scheduleViewportUpdate(recalculate = false) {
      if (this.rafId) return;
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = 0;
        if (recalculate) this.updateLayoutMetrics();
        this.updateViewportState();
      });
    }

    updateLayoutMetrics() {
      const headerOffset = this.getVisibleHeaderHeight();
      this.style.setProperty('--vni-pc-header-offset', `${Math.round(headerOffset)}px`);

      const rect = this.getBoundingClientRect();
      this.documentStart = window.scrollY + rect.top;
      this.documentEnd = this.documentStart + this.offsetHeight;
    }

    getVisibleHeaderHeight() {
      if (!this.headerElement) return 0;
      const rect = this.headerElement.getBoundingClientRect();
      if (rect.bottom <= 0 || rect.top >= window.innerHeight) return 0;
      return Math.max(0, Math.min(rect.bottom, rect.height));
    }

    updateViewportState() {
      const headerOffset = this.getVisibleHeaderHeight();
      this.style.setProperty('--vni-pc-header-offset', `${Math.round(headerOffset)}px`);
      this.updateReadingProgress();
      this.updateActiveSection(headerOffset);
      this.updateFloatingTop();
    }

    updateReadingProgress() {
      if (!this.progressElement) return;

      const start = Number.isFinite(this.documentStart)
        ? this.documentStart
        : window.scrollY + this.getBoundingClientRect().top;
      const end = Number.isFinite(this.documentEnd)
        ? this.documentEnd
        : start + this.offsetHeight;
      const track = Math.max(1, end - start - window.innerHeight);
      const value = Math.min(1, Math.max(0, (window.scrollY - start) / track));
      this.progressElement.style.width = `${(value * 100).toFixed(2)}%`;
    }

    updateActiveSection(headerOffset) {
      if (!this.sections.length) return;

      const threshold = headerOffset + 128;
      let activeSection = this.sections[0];

      for (const section of this.sections) {
        if (section.getBoundingClientRect().top <= threshold) {
          activeSection = section;
        } else {
          break;
        }
      }

      if (!activeSection || activeSection.id === this.currentSectionId) return;
      this.currentSectionId = activeSection.id;

      this.links.forEach((link) => {
        const active = link.getAttribute('href') === `#${this.currentSectionId}`;
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });

      if (this.jumpSelect) {
        const option = Array.from(this.jumpSelect.options).find(
          (item) => item.value === `#${this.currentSectionId}`
        );
        if (option) this.jumpSelect.value = option.value;
      }
    }

    updateFloatingTop() {
      if (!this.floatingTop) return;
      const rootRect = this.getBoundingClientRect();
      const shouldShow = rootRect.top < -650 && rootRect.bottom > 180;
      this.floatingTop.hidden = !shouldShow;
    }

    bindAnchorNavigation() {
      this.addEventListener(
        'click',
        (event) => {
          const anchor = event.target.closest('a[href^="#"]');
          if (!anchor || !this.contains(anchor)) return;

          const selector = anchor.getAttribute('href');
          if (!selector || selector === '#') return;

          let target = null;
          try {
            target = this.querySelector(selector);
          } catch (error) {
            return;
          }

          if (!target) return;
          event.preventDefault();
          this.scrollToElement(target);

          if (history.replaceState) {
            history.replaceState(null, '', selector);
          }
        },
        { signal: this.signal }
      );

      this.jumpSelect?.addEventListener(
        'change',
        () => {
          const selector = this.jumpSelect.value;
          if (!selector) return;
          const target = this.querySelector(selector);
          if (!target) return;
          this.scrollToElement(target);
          if (history.replaceState) history.replaceState(null, '', selector);
        },
        { signal: this.signal }
      );

      this.floatingTop?.addEventListener(
        'click',
        () => {
          const title = this.querySelector('[id^="VNIPolicyTitle-"]') || this;
          this.scrollToElement(title, 16);
        },
        { signal: this.signal }
      );
    }

    scrollToElement(element, extraOffset = 18) {
      const headerOffset = this.getVisibleHeaderHeight();
      const utility = this.querySelector('[data-vni-policy-utility]');
      const utilityHeight = utility && getComputedStyle(utility).position === 'sticky'
        ? utility.getBoundingClientRect().height
        : 0;
      const top = window.scrollY + element.getBoundingClientRect().top - headerOffset - utilityHeight - extraOffset;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth'
      });
    }

    bindUtilityControls() {
      this.printButton?.addEventListener('click', () => window.print(), { signal: this.signal });

      this.readingModeButton?.addEventListener(
        'click',
        () => {
          const enabled = !this.classList.contains('is-reading-mode');
          this.setReadingMode(enabled, true);
        },
        { signal: this.signal }
      );

      if (this.searchInput) {
        this.searchInput.addEventListener(
          'input',
          () => {
            window.clearTimeout(this.searchTimer);
            this.searchTimer = window.setTimeout(() => this.runSearch(this.searchInput.value), 90);
          },
          { signal: this.signal }
        );

        this.searchInput.addEventListener(
          'keydown',
          (event) => {
            if (event.key === 'Escape') {
              event.preventDefault();
              this.clearSearch(true);
              return;
            }

            if (event.key === 'Enter' && this.searchMarks.length) {
              event.preventDefault();
              this.moveSearchCursor(event.shiftKey ? -1 : 1);
            }
          },
          { signal: this.signal }
        );
      }

      this.searchClear?.addEventListener('click', () => this.clearSearch(true), {
        signal: this.signal
      });
    }

    restoreReadingMode() {
      let enabled = false;
      try {
        enabled = window.localStorage.getItem(STORAGE_KEY) === 'true';
      } catch (error) {
        enabled = false;
      }
      this.setReadingMode(enabled, false);
    }

    setReadingMode(enabled, announce) {
      this.classList.toggle('is-reading-mode', enabled);
      this.readingModeButton?.setAttribute('aria-pressed', String(enabled));

      try {
        window.localStorage.setItem(STORAGE_KEY, String(enabled));
      } catch (error) {
        // The page remains fully functional when storage is unavailable.
      }

      this.updateLayoutMetrics();
      if (announce) this.showToast(enabled ? 'Reading mode enabled.' : 'Reading mode disabled.');
    }

    runSearch(rawValue) {
      const query = rawValue.trim();
      this.clearSearchHighlights();
      this.sections.forEach((section) => section.classList.remove('is-search-match', 'is-search-dimmed'));
      this.searchCursor = -1;

      if (this.searchClear) this.searchClear.hidden = query.length === 0;

      if (query.length < 2) {
        if (this.searchCount) this.searchCount.textContent = '';
        return;
      }

      const normalizedQuery = query.toLocaleLowerCase();
      const matchingSections = [];

      this.sections.forEach((section) => {
        const matches = section.textContent.toLocaleLowerCase().includes(normalizedQuery);
        section.classList.toggle('is-search-match', matches);
        section.classList.toggle('is-search-dimmed', !matches);
        if (matches) matchingSections.push(section);
      });

      matchingSections.forEach((section) => this.highlightTerm(section, query));
      this.searchMarks = Array.from(this.querySelectorAll('mark[data-vni-search-mark]'));

      if (this.searchCount) {
        const count = this.searchMarks.length;
        this.searchCount.textContent = count === 1 ? '1 match' : `${count} matches`;
      }

      if (this.searchMarks.length) {
        this.searchCursor = 0;
        this.focusSearchMark(0, false);
      }
    }

    highlightTerm(root, query) {
      const safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(safeQuery, 'gi');
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => {
          if (!node.nodeValue || !regex.test(node.nodeValue)) {
            regex.lastIndex = 0;
            return NodeFilter.FILTER_REJECT;
          }
          regex.lastIndex = 0;

          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;
          if (parent.closest('script, style, mark, button, input, textarea, select, option, svg, form.vni-pc__request-form')) {
            return NodeFilter.FILTER_REJECT;
          }
          return NodeFilter.FILTER_ACCEPT;
        }
      });

      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);

      nodes.forEach((node) => {
        if (this.querySelectorAll('mark[data-vni-search-mark]').length >= 400) return;

        const text = node.nodeValue;
        const fragment = document.createDocumentFragment();
        let lastIndex = 0;
        let match;
        regex.lastIndex = 0;

        while ((match = regex.exec(text)) !== null) {
          if (match.index > lastIndex) {
            fragment.append(document.createTextNode(text.slice(lastIndex, match.index)));
          }

          const mark = document.createElement('mark');
          mark.dataset.vniSearchMark = '';
          mark.textContent = match[0];
          fragment.append(mark);
          lastIndex = match.index + match[0].length;

          if (match[0].length === 0) regex.lastIndex += 1;
        }

        if (lastIndex < text.length) {
          fragment.append(document.createTextNode(text.slice(lastIndex)));
        }

        node.replaceWith(fragment);
      });
    }

    moveSearchCursor(direction) {
      if (!this.searchMarks.length) return;
      this.searchCursor = (this.searchCursor + direction + this.searchMarks.length) % this.searchMarks.length;
      this.focusSearchMark(this.searchCursor, true);
    }

    focusSearchMark(index, announce) {
      const mark = this.searchMarks[index];
      if (!mark) return;

      this.searchMarks.forEach((item) => item.removeAttribute('data-vni-search-current'));
      mark.setAttribute('data-vni-search-current', '');
      this.scrollToElement(mark, 80);

      if (announce && this.searchCount) {
        this.searchCount.textContent = `${index + 1} of ${this.searchMarks.length}`;
      }
    }

    clearSearch(focusInput = false) {
      if (this.searchInput) this.searchInput.value = '';
      if (this.searchClear) this.searchClear.hidden = true;
      if (this.searchCount) this.searchCount.textContent = '';
      this.clearSearchHighlights();
      this.sections.forEach((section) => section.classList.remove('is-search-match', 'is-search-dimmed'));
      this.searchMarks = [];
      this.searchCursor = -1;
      if (focusInput) this.searchInput?.focus();
    }

    clearSearchHighlights() {
      this.querySelectorAll('mark[data-vni-search-mark]').forEach((mark) => {
        const parent = mark.parentNode;
        mark.replaceWith(document.createTextNode(mark.textContent || ''));
        parent?.normalize();
      });
    }

    bindCopyControls() {
      this.querySelectorAll('[data-vni-policy-copy-link]').forEach((button) => {
        button.addEventListener(
          'click',
          async () => {
            const hash = button.getAttribute('data-vni-policy-copy-link');
            if (!hash) return;
            const url = `${window.location.origin}${window.location.pathname}${window.location.search}${hash}`;
            const copied = await this.copyText(url);
            this.showToast(copied ? 'Section link copied.' : 'Unable to copy the section link.');
          },
          { signal: this.signal }
        );
      });

      const copyConsent = this.querySelector('[data-vni-policy-copy-consent]');
      const consentText = this.querySelector('[data-vni-policy-consent-copy]');

      copyConsent?.addEventListener(
        'click',
        async () => {
          const text = (consentText?.textContent || '').trim().replace(/\s+/g, ' ');
          if (!text) return;
          const copied = await this.copyText(text);
          this.showToast(copied ? 'Consent language copied.' : 'Unable to copy the consent language.');
        },
        { signal: this.signal }
      );
    }

    async copyText(text) {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          return true;
        }
      } catch (error) {
        // Continue to the selection fallback.
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      textarea.style.pointerEvents = 'none';
      document.body.append(textarea);
      textarea.select();

      let copied = false;
      try {
        copied = document.execCommand('copy');
      } catch (error) {
        copied = false;
      }

      textarea.remove();
      return copied;
    }

    bindRequestForm() {
      const form = this.querySelector('form.vni-pc__request-form');
      if (!form) return;

      const characterField = form.querySelector('[data-vni-policy-character-field]');
      const characterCount = form.querySelector('[data-vni-policy-character-count]');
      const submitButton = form.querySelector('[data-vni-policy-submit]');

      const updateCount = () => {
        if (!characterField || !characterCount) return;
        characterCount.textContent = `${characterField.value.length} / 1500`;
      };

      characterField?.addEventListener('input', updateCount, { signal: this.signal });
      updateCount();

      form.addEventListener(
        'submit',
        (event) => {
          if (!form.checkValidity()) {
            event.preventDefault();
            form.reportValidity();
            return;
          }

          if (submitButton) {
            submitButton.setAttribute('aria-busy', 'true');
            const label = submitButton.querySelector('span');
            if (label) label.textContent = 'Submitting';
          }
        },
        { signal: this.signal }
      );
    }

    showToast(message) {
      if (!this.toast) return;
      window.clearTimeout(this.toastTimer);
      this.toast.textContent = message;
      this.toast.hidden = false;
      this.toastTimer = window.setTimeout(() => {
        this.toast.hidden = true;
      }, 2600);
    }
  }

  if (!customElements.get(ELEMENT_NAME)) {
    customElements.define(ELEMENT_NAME, VNIPrivacyCompliance);
  }
})();
